/*
 * mantraa-rashi.js
 * Interactive rashi selector + PDP conversion modules for Mantraa.
 * Vanilla, no dependencies. Data comes from sections/mantraa-rashi.liquid.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('mantraa-rashi')) return;

  var SPACING = 46;       // px between adjacent signs along the dial
  var ARC_RADIUS = 620;   // larger = flatter arc
  var FADE_AFTER = 3;     // signs each side kept fully lit

  function mod(n, m) { return ((n % m) + m) % m; }
  function easeOutCubic(p) { return 1 - Math.pow(1 - p, 3); }
  function setText(el, v) { if (el && v != null) el.textContent = v; }
  function pad(n) { return String(n).padStart(2, '0'); }

  var MONTHS = { jan:1, feb:2, mar:3, apr:4, may:5, jun:6,
                 jul:7, aug:8, sep:9, oct:10, nov:11, dec:12 };

  /*
   * Read "Jul 23 - Aug 22" into numeric bounds. Signs wrap the year end
   * (Capricorn is Dec 22 - Jan 19), which the comparison below allows for.
   * Returns null if the text has been edited into something unparseable, and
   * the finder simply skips that sign rather than guessing.
   */
  function parseRange(text) {
    if (!text) return null;
    var m = String(text).toLowerCase().match(
      /([a-z]{3})[a-z]*\s+(\d{1,2})\s*[-\u2013\u2014to]+\s*([a-z]{3})[a-z]*\s+(\d{1,2})/
    );
    if (!m) return null;
    var fromM = MONTHS[m[1]], toM = MONTHS[m[3]];
    if (!fromM || !toM) return null;
    return { fromM: fromM, fromD: +m[2], toM: toM, toD: +m[4] };
  }

  function inRange(r, month, day) {
    if (!r) return false;
    var after = month > r.fromM || (month === r.fromM && day >= r.fromD);
    var before = month < r.toM || (month === r.toM && day <= r.toD);
    // A range that crosses new year is satisfied by either half.
    return (r.fromM > r.toM) ? (after || before) : (after && before);
  }

  var DAYS_IN = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  /*
   * Seeded pseudo-random stars. A fixed seed keeps the sky identical on every
   * render, so the layout never jumps between paints.
   */
  function makeStars(count, seed) {
    var out = [], r = seed || 1;
    function rnd() { r = (r * 9301 + 49297) % 233280; return r / 233280; }
    for (var i = 0; i < count; i++) {
      out.push({
        left: (rnd() * 100).toFixed(2),
        top: (rnd() * 100).toFixed(2),
        size: (rnd() * 1.9 + 1).toFixed(2),
        dur: (rnd() * 4 + 2.6).toFixed(1),
        delay: (rnd() * 4).toFixed(1)
      });
    }
    return out;
  }

  var HOOKS = [
    'heroA', 'heroB', 'heroGlow', 'dial', 'signName', 'hindi', 'dates', 'chips',
    'price', 'compare', 'save', 'variantId', 'form', 'add', 'buy', 'addLabel',
    'soldOut', 'countdown', 'countdownClock', 'upsell', 'storyImage', 'storyName',
    'storyText', 'stoneAName', 'stoneAText', 'stoneBName', 'stoneBText',
    'gridHost', 'bar', 'barName', 'barPrice', 'barCompare', 'finalRef',
    'eddInput', 'eddBtn', 'eddOut', 'upsellWrap', 'hintDates', 'element',
    'finderMonth', 'finderDay', 'finderOut', 'trioHinglish', 'trioPlanet',
    'trioPlanetGlyph', 'trioRashiGlyph', 'trioCrystals',
    'heroName', 'heroHindi', 'valueName', 'valuePrice', 'valueWas', 'valueTotal', 'valueSave',
    'stars', 'constelLine', 'constelStars', 'rail', 'benefits',
    'benefitsTitle'
  ];

  class MantraaRashi extends HTMLElement {

    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.signs = JSON.parse(this.querySelector('[data-mr-signs]').textContent);
      } catch (err) { return; }
      if (!this.signs.length) return;

      this.count = this.signs.length;
      this.total = this.count * SPACING;
      this.half = this.total / 2;
      this.order = this.signs.map(function (s) { return s.key; });
      this.byKey = {};
      this.signs.forEach(function (s) { this.byKey[s.key] = s; }, this);

      this.reduce = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.$ = {};
      HOOKS.forEach(function (k) {
        this.$[k] = this.querySelector('[data-mr="' + k + '"]');
      }, this);

      this.submitButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-mr-submit]')
      );

      this.selected = this._initialSign();
      this.offset = this.order.indexOf(this.selected) * SPACING;
      this._touched = false;
      this._dragMoved = false;

      this.railCaptions = (this.getAttribute('data-rail-captions') || '')
        .split('|').map(function (c) { return c.trim(); }).filter(Boolean);

      this._buildDial();
      this._buildGrid();
      this._bind();
      this.renderDial();
      this.applySign(this.selected, { initial: true });
      var stockHost = this.querySelector('[data-mr-stock][data-stock-mode="simulated"]');
      if (stockHost) this._stockTimer = simulatedStock(stockHost);

      this._paintStars();
      this._startNudge();
      this._startCountdown();
      this._initOffers();
      this._initEdd();
      this._initFinder();
      this._onScroll();

      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._onScroll);
      document.removeEventListener('visibilitychange', this._onVisible);
      cancelAnimationFrame(this._raf);
      cancelAnimationFrame(this._nudgeRaf);
      clearTimeout(this._nudgeTimer);
      clearInterval(this._nudgeInterval);
      clearInterval(this._cdInterval);
      clearInterval(this._stockTimer);
      clearTimeout(this._heroTimer);
    }

    /* ---------------------------------------------------------------- init */

    _initialSign() {
      var fallback = this.getAttribute('data-default') || '';
      var lock = this.getAttribute('data-lock-default') === 'true';

      var fromUrl = '';
      try {
        var q = (new URLSearchParams(location.search).get('sign') || '').trim().toLowerCase();
        if (this.byKey[q]) fromUrl = q;
      } catch (err) {}
      if (fromUrl) return fromUrl;

      if (lock && this.byKey[fallback]) return fallback;

      var saved = '';
      try { saved = localStorage.getItem('mantraa_rashi') || ''; } catch (err) {}
      if (this.byKey[saved]) return saved;

      return this.byKey[fallback] ? fallback : this.order[0];
    }

    _buildDial() {
      var host = this.$.dial;
      if (!host) return;
      var html = '';
      this.signs.forEach(function (s) {
        html += '<button class="mr-dial-sign" type="button" data-mr-dial data-key="'
          + s.key + '" aria-label="' + s.name + '">'
          + '<span class="mr-dial-circle"><span class="mr-dial-glyph">' + s.glyph + '︎</span></span>'
          + '<span class="mr-dial-name">' + s.name + '</span></button>';
      });
      host.insertAdjacentHTML('beforeend', html);
      this.dialButtons = Array.prototype.slice.call(this.querySelectorAll('[data-mr-dial]'));
    }

    _buildGrid() {
      var host = this.$.gridHost;
      if (!host) return;
      var html = '';
      this.signs.forEach(function (s) {
        html += '<button class="mr-card" type="button" data-mr-grid data-key="' + s.key + '">'
          + '<span class="mr-card-thumb" style="background-image:url(\'' + s.image + '\')"></span>'
          + '<span class="mr-card-foot"><span><span class="mr-card-name">' + s.name + '</span>'
          + '<span class="mr-card-dates" style="display:block">' + s.dates + '</span></span>'
          + '<span class="mr-card-glyph">' + s.glyph + '︎</span></span></button>';
      });
      host.innerHTML = html;
      this.gridCards = Array.prototype.slice.call(this.querySelectorAll('[data-mr-grid]'));
    }

    _bind() {
      var self = this;
      var dial = this.$.dial;

      if (dial) {
        dial.addEventListener('pointerdown', this.onPointerDown.bind(this));
        dial.addEventListener('pointermove', this.onPointerMove.bind(this));
        dial.addEventListener('pointerup', this.onPointerUp.bind(this));
        dial.addEventListener('pointercancel', this.onPointerUp.bind(this));
      }

      (this.dialButtons || []).forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (self._dragMoved) return;
          self._endNudge();
          self.bringToCentre(btn.getAttribute('data-key'));
        });
      });

      (this.gridCards || []).forEach(function (card) {
        card.addEventListener('click', function () {
          self._endNudge();
          self.bringToCentre(card.getAttribute('data-key'));
          self.applySign(card.getAttribute('data-key'), { persist: true });
          var stage = self.$.dial;
          if (stage) stage.scrollIntoView({ behavior: self.reduce ? 'auto' : 'smooth', block: 'center' });
        });
      });

      if (this.$.form) {
        this.$.form.addEventListener('submit', function (e) {
          self._checkout(e, false);
        });
      }
      if (this.$.buy) {
        this.$.buy.addEventListener('click', function (e) { self._checkout(e, true); });
      }
      this.submitButtons.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          self._checkout(e, btn.getAttribute('data-mr-submit') === 'buy');
        });
      });

      this._onVisible = function () {
        if (document.hidden) return;
        // Re-sync the sticky bar as soon as the tab comes back.
        self._scrollCheck();
        if (self._drag) return;
        var snapped = Math.round(self.offset / SPACING) * SPACING;
        if (self.offset !== snapped) self._settle(snapped);
      };
      document.addEventListener('visibilitychange', this._onVisible);

      this._onResize = this.renderDial.bind(this);
      this._onScroll = this._rafThrottle(this._scrollCheck.bind(this));
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    _rafThrottle(fn) {
      var ticking = false;
      return function () {
        // rAF never fires in a background tab, which would leave the sticky
        // bar stuck in whatever state it had when the tab was hidden.
        if (document.hidden) { fn(); return; }
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; fn(); });
      };
    }

    /* ---------------------------------------------------------------- drag */

    onPointerDown(e) {
      this._endNudge();
      cancelAnimationFrame(this._raf);
      this._drag = { x: e.clientX, offset: this.offset, id: e.pointerId, target: e.currentTarget, captured: false };
      this._dragMoved = false;
      this.$.dial.classList.add('is-dragging');
    }

    onPointerMove(e) {
      if (!this._drag) return;
      var dx = e.clientX - this._drag.x;
      if (Math.abs(dx) > 5 && !this._drag.captured) {
        this._dragMoved = true;
        try { this._drag.target.setPointerCapture(this._drag.id); } catch (err) {}
        this._drag.captured = true;
      }
      this.offset = this._drag.offset - dx;
      this.renderDial();
      this.applySign(this.order[this.centreIndex(this.offset)], {});
    }

    onPointerUp() {
      if (!this._drag) return;
      this._drag = null;
      this.$.dial.classList.remove('is-dragging');
      this.animateTo(Math.round(this.offset / SPACING) * SPACING);
      var self = this;
      setTimeout(function () { self._dragMoved = false; }, 40);
    }

    centreIndex(offset) { return mod(Math.round(offset / SPACING), this.count); }

    bringToCentre(key) {
      var i = this.order.indexOf(key);
      if (i < 0) return;
      this.animateTo(i * SPACING + Math.round((this.offset - i * SPACING) / this.total) * this.total);
    }

    animateTo(target) {
      cancelAnimationFrame(this._raf);
      var self = this;

      // rAF is frozen in a background tab; jumping avoids stranding the dial
      // between two signs.
      if (this.reduce || document.hidden) { this._settle(target); return; }

      var start = this.offset;
      var t0 = performance.now();

      function step(now) {
        var p = Math.min(1, (now - t0) / 360);
        self.offset = start + (target - start) * easeOutCubic(p);
        self.renderDial();
        self.applySign(self.order[self.centreIndex(self.offset)], {});
        if (p < 1 && !document.hidden) self._raf = requestAnimationFrame(step);
        else self._settle(target);
      }
      this._raf = requestAnimationFrame(step);
    }

    _settle(target) {
      this.offset = target;
      this.renderDial();
      this.applySign(this.order[this.centreIndex(target)], { persist: true });
    }

    /*
     * Lay signs along a virtual arc. One number (this.offset) drives position,
     * rotation, scale and fade — all derived from distance to centre.
     */
    renderDial() {
      var buttons = this.dialButtons || [];
      var fadeEdge = FADE_AFTER * SPACING;

      for (var i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        var x = mod(i * SPACING - this.offset + this.half, this.total) - this.half;
        var d = Math.abs(x);
        var opacity = d <= fadeEdge
          ? Math.min(1, 1.05 - d / 600)
          : Math.max(0, 1.05 - fadeEdge / 600 - (d - fadeEdge) / 110);

        btn.style.left = 'calc(50% + ' + x.toFixed(1) + 'px)';
        btn.style.top = (14 + (d * d) / 720).toFixed(1) + 'px';
        btn.style.transform = 'translateX(-50%) rotate('
          + (Math.atan2(x, ARC_RADIUS) * 180 / Math.PI).toFixed(2) + 'deg) scale('
          + Math.max(0.66, 1.14 - d / 400).toFixed(3) + ')';
        btn.style.opacity = opacity.toFixed(2);
        btn.style.zIndex = Math.round(100 - d);
        btn.style.pointerEvents = opacity < 0.1 ? 'none' : 'auto';
        btn.classList.toggle('is-on', d < SPACING / 2);
      }
    }

    /* --------------------------------------------------------------- state */

    applySign(key, opts) {
      var s = this.byKey[key];
      if (!s) return;
      opts = opts || {};

      var changed = this.selected !== key;
      if (!changed && !opts.initial) {
        if (opts.persist) this._persist(key);
        return;
      }
      this.selected = key;

      this.style.setProperty('--mr-accent', s.accent);
      this.style.setProperty('--mr-stone-a', s.stoneAColor);
      this.style.setProperty('--mr-stone-b', s.stoneBColor);

      this._swapHero(s.image, !opts.initial && !this.reduce);

      setText(this.$.signName, s.name);
      setText(this.$.hindi, s.hindi);
      setText(this.$.dates, s.dates);
      setText(this.$.element, s.element);
      setText(this.$.storyName, s.name);
      setText(this.$.storyText, s.story);
      setText(this.$.stoneAName, s.stoneAName);
      setText(this.$.stoneAText, s.stoneAText);
      setText(this.$.stoneBName, s.stoneBName);
      setText(this.$.stoneBText, s.stoneBText);
      setText(this.$.addLabel, 'Add ' + s.name + ' to cart');
      setText(this.$.barName, s.name + ' Bracelet');

      if (this.$.chips) {
        this.$.chips.innerHTML = (s.chips || []).map(function (c) {
          return '<span class="mr-chip">' + c + '</span>';
        }).join('');
      }

      if (this.$.storyImage) {
        this.$.storyImage.style.backgroundImage = "url('" + (s.lifestyle || s.image) + "')";
      }

      // Rashi / ruling planet / crystals trio
      setText(this.$.heroName, s.name);
      setText(this.$.heroHindi, s.hindi);
      setText(this.$.valueName, s.name + ' Bracelet');
      setText(this.$.benefitsTitle, 'What it does for ' + s.name);
      this._renderRail(s);
      this._renderBenefits(s);
      if (changed || opts.initial) this._drawConstellation(s.constel);
      setText(this.$.hintDates, s.dates);
      setText(this.$.trioRashiGlyph, s.glyph + '\uFE0E');
      setText(this.$.trioHinglish, s.hinglish);
      setText(this.$.trioPlanet, s.planet);
      setText(this.$.trioPlanetGlyph, s.planetGlyph ? s.planetGlyph + '\uFE0E' : '');
      setText(this.$.trioCrystals, s.stoneAName + ' \u00B7 ' + s.stoneBName);

      // The energised add-on can be switched off for individual rashis.
      if (this.$.upsellWrap) this.$.upsellWrap.hidden = !!s.hideUpsell;

      (this.gridCards || []).forEach(function (card) {
        card.classList.toggle('is-on', card.getAttribute('data-key') === key);
      });

      this._updateBuy(s);
      if (opts.persist) this._persist(key);
    }

    _persist(key) {
      try { localStorage.setItem('mantraa_rashi', key); } catch (err) {}
      try {
        var url = new URL(location.href);
        url.searchParams.set('sign', key);
        history.replaceState(null, '', url.toString());
      } catch (err) {}
    }

    _updateBuy(s) {
      setText(this.$.price, s.price);
      setText(this.$.barPrice, s.price);

      [this.$.compare, this.$.barCompare].forEach(function (el) {
        if (!el) return;
        if (s.compare) { el.textContent = s.compare; el.hidden = false; }
        else el.hidden = true;
      });

      if (this.$.save) {
        if (s.savePercent) { this.$.save.textContent = 'Save ' + s.savePercent + '%'; this.$.save.hidden = false; }
        else this.$.save.hidden = true;
      }

      setText(this.$.valuePrice, s.price);
      setText(this.$.valueTotal, s.price);
      if (this.$.valueWas) {
        this.$.valueWas.textContent = s.compare || '';
        this.$.valueWas.hidden = !s.compare;
      }
      if (this.$.valueSave) {
        /*
         * Work from the numeric amounts the section prints, never from the
         * formatted string — this shop's money format is "₹. 1,099.00" and
         * parseFloat on the stripped version silently returns 0.1099.
         */
        var host = this.$.valueSave;
        var extra = parseFloat(host.getAttribute('data-extra') || '0') || 0;
        // Ignore a compare-at far above the price — that is a data slip,
        // not a discount, and would advertise a saving that is not real.
        var base = (s.compareAmount && s.compareAmount > s.priceAmount
                    && s.compareAmount <= s.priceAmount * 5)
          ? (s.compareAmount - s.priceAmount) : 0;
        var total = Math.round(base + extra);
        if (total > 0) {
          host.textContent = (host.getAttribute('data-prefix') || "You're saving")
            + ' \u20B9' + total.toLocaleString('en-IN') + ' '
            + (host.getAttribute('data-suffix') || 'on this order');
          host.hidden = false;
        } else {
          host.hidden = true;
        }
      }

      if (this.$.variantId) this.$.variantId.value = s.variantId;

      var soldOut = !s.available;

      // A stock counter beside a Sold out button contradicts itself.
      var stockHost = this.querySelector('[data-mr-stock]');
      if (stockHost) stockHost.hidden = soldOut;

      [this.$.add, this.$.buy].concat(this.submitButtons).forEach(function (btn) {
        if (btn) btn.disabled = soldOut;
      });
      if (this.$.soldOut) this.$.soldOut.hidden = !soldOut;
      if (soldOut) setText(this.$.addLabel, 'Sold out');
    }

    _swapHero(url, animate) {
      var a = this.$.heroA, b = this.$.heroB;
      if (!a || !b) return;

      var front = this._heroFront || a;
      var next = front === a ? b : a;

      next.style.backgroundImage = "url('" + url + "')";
      next.style.zIndex = '2';
      front.style.zIndex = '1';
      this._heroFront = next;

      if (!animate) {
        next.classList.remove('is-in');
        next.style.opacity = '1';
        front.style.opacity = '0';
        return;
      }

      next.style.opacity = '';
      next.classList.remove('is-in');
      void next.offsetWidth;
      next.classList.add('is-in');

      if (this.$.heroGlow) {
        this.$.heroGlow.classList.remove('is-in');
        void this.$.heroGlow.offsetWidth;
        this.$.heroGlow.classList.add('is-in');
      }

      var self = this;
      clearTimeout(this._heroTimer);
      this._heroTimer = setTimeout(function () {
        next.classList.remove('is-in');
        next.style.opacity = '1';
        front.style.opacity = '0';
        if (self.$.heroGlow) self.$.heroGlow.classList.remove('is-in');
      }, 640);
    }

    /* ------------------------------------------------------------ checkout */

    /*
     * One path for every buy button. Adds through /cart/add.js so the optional
     * energised add-on rides along in the same request, then hands off to
     * whatever the theme already uses: Shrine's cart drawer for Add to cart,
     * GoKwik for Buy Now. Every hand-off degrades to a plain URL.
     */
    _checkout(e, straightToCheckout) {
      var sign = this.byKey[this.selected];
      if (!sign || !sign.available) return;
      if (e) e.preventDefault();

      var items = [{ id: sign.variantId, quantity: 1 }];

      var up = this.$.upsell;
      var upsellOn = up && up.checked && up.value
        && !(this.$.upsellWrap && this.$.upsellWrap.hidden);
      if (upsellOn) items.push({ id: parseInt(up.value, 10), quantity: 1 });

      /*
       * Ask Shopify to render the cart drawer's sections alongside the add.
       * Without this the drawer opens still showing its server-rendered empty
       * state, which reads to the customer as "nothing happened".
       */
      var payload = { items: items };
      var drawer = document.querySelector('cart-drawer, cart-notification');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try {
          payload.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
          payload.sections_url = window.location.pathname;
        } catch (err) { /* fall back to a plain add */ }
      }

      var btn = straightToCheckout ? this.$.buy : this.$.add;
      var restore = btn ? btn.innerHTML : null;
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }

      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function (data) {
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = restore; }
          if (straightToCheckout) self._openCheckout();
          else self._openCart(data);
        })
        .catch(function () {
          // Network or app conflict: let the browser do a normal form post.
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = restore; }
          if (self.$.form) self.$.form.submit();
        });
    }

    /* Hand off to GoKwik exactly as the theme's own Buy Now does. */
    _openCheckout() {
      try {
        if (typeof window.triggerGokwikCustomCheckout === 'function') {
          window.triggerGokwikCustomCheckout();
          return;
        }
        if (window.gokwikSdk && typeof window.gokwikSdk.initCheckout === 'function') {
          window.gokwikSdk.initCheckout();
          return;
        }
      } catch (err) { /* fall through to Shopify checkout */ }
      window.location.href = '/checkout';
    }

    /*
     * Hand the freshly rendered sections to the theme's own drawer. If the
     * drawer does not actually become visible — a themed drawer can fail
     * quietly — fall through to /cart so the customer is never left looking
     * at an unchanged page after a successful add.
     */
    _openCart(data) {
      if (this.getAttribute('data-after-add') === 'cart') {
        window.location.href = '/cart';
        return;
      }

      var drawer = document.querySelector('cart-drawer, cart-notification');
      if (!drawer) { window.location.href = '/cart'; return; }

      try {
        if (data && data.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
        } else if (typeof drawer.open === 'function') {
          drawer.open();
        } else {
          window.location.href = '/cart';
          return;
        }
        // Shrine puts is-empty on the host element, but its renderContents
        // only clears that flag from an inner node.
        drawer.classList.remove('is-empty');
        if (typeof window.publish === 'function') {
          try { window.publish('cart-update', { source: 'mantraa-rashi' }); } catch (err) {}
        }
      } catch (err) {
        window.location.href = '/cart';
        return;
      }

      setTimeout(function () {
        var visible = false;
        try {
          var cs = getComputedStyle(drawer);
          visible = cs.visibility !== 'hidden'
            && cs.display !== 'none'
            && drawer.getBoundingClientRect().width > 0;
        } catch (err) {}
        if (!visible) window.location.href = '/cart';
      }, 800);
    }

    /* ------------------------------------------------------------- modules */

    /*
     * Countdown to a real deadline set in the theme editor. When it passes the
     * block hides itself — it never restarts per visitor.
     */
    /*
     * Two honest modes:
     *   fixed — counts to a real date you set; hides itself once it passes.
     *   daily — counts to today's cut-off in IST. Same instant for every
     *           visitor, so it is a real deadline, not a per-person fake.
     */
    _startCountdown() {
      var wrap = this.$.countdown;
      var clock = this.$.countdownClock;
      if (!wrap || !clock) return;

      var mode = this.getAttribute('data-countdown-mode') || 'off';
      if (mode === 'off') return;

      var self = this;
      var resetHour = parseInt(this.getAttribute('data-countdown-hour') || '23', 10);

      function istDeadline(hour) {
        var now = new Date();
        var shift = (330 + now.getTimezoneOffset()) * 60000; // local -> IST
        var ist = new Date(now.getTime() + shift);
        ist.setHours(hour, 59, 59, 999);
        if (ist.getTime() <= now.getTime() + shift) ist.setDate(ist.getDate() + 1);
        return ist.getTime() - shift;
      }

      function target() {
        if (mode === 'daily') return istDeadline(resetHour);
        return Date.parse(self.getAttribute('data-deadline') || '');
      }

      var end = target();
      if (isNaN(end)) return;

      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          if (mode === 'daily') { end = target(); return; }
          wrap.classList.remove('is-live');
          clearInterval(self._cdInterval);
          return;
        }
        var h = Math.floor(left / 3600000);
        var m = Math.floor((left % 3600000) / 60000);
        var sec = Math.floor((left % 60000) / 1000);
        clock.textContent = pad(h) + 'h : ' + pad(m) + 'm : ' + pad(sec) + 's';
        wrap.classList.add('is-live');
      }
      tick();
      this._cdInterval = setInterval(tick, 1000);
    }

    _initOffers() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-mr-copy]'), function (btn) {
        btn.addEventListener('click', function () {
          var code = btn.getAttribute('data-mr-copy');
          var done = function () {
            var was = btn.textContent;
            btn.textContent = 'Copied';
            btn.classList.add('is-done');
            setTimeout(function () { btn.textContent = was; btn.classList.remove('is-done'); }, 1600);
          };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(done, done);
          } else {
            var t = document.createElement('textarea');
            t.value = code;
            document.body.appendChild(t);
            t.select();
            try { document.execCommand('copy'); } catch (err) {}
            document.body.removeChild(t);
            done();
          }
        });
      });
    }

    /*
     * Delivery estimate from a pincode-prefix table set in the editor, plus a
     * same-day cutoff hour. Everything shown here is derived from real
     * settings — no invented countdowns.
     */
    _initEdd() {
      var input = this.$.eddInput, btn = this.$.eddBtn, out = this.$.eddOut;
      if (!input || !btn || !out) return;

      var zones = {};
      try { zones = JSON.parse(this.getAttribute('data-edd-zones') || '{}'); } catch (err) {}
      var cutoff = parseInt(this.getAttribute('data-edd-cutoff') || '14', 10);
      var token = this.getAttribute('data-gk-token') || '';
      var mid = this.getAttribute('data-gk-mid') || '';
      var self = this;

      function show(msg, isError) {
        out.innerHTML = msg;
        out.classList.add('is-shown');
        out.classList.toggle('is-error', !!isError);
      }

      function cookie(name) {
        var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
        return m ? m[2] : '';
      }

      /* Offline table — used when GoKwik is not configured or does not answer. */
      function localEstimate(pin) {
        var days = null;
        for (var len = 3; len >= 1; len--) {
          var k = pin.slice(0, len);
          if (zones[k] != null) { days = zones[k]; break; }
        }
        if (days == null) days = zones['default'] != null ? zones['default'] : 5;

        var now = new Date();
        var ship = new Date(now);
        if (now.getHours() >= cutoff) ship.setDate(ship.getDate() + 1);
        ship.setDate(ship.getDate() + Number(days));
        if (ship.getDay() === 0) ship.setDate(ship.getDate() + 1);

        var fmt = ship.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
        show('Free Delivery by <b>' + fmt + '</b>', false);
      }

      function check() {
        var pin = (input.value || '').replace(/\D/g, '');
        // Indian PIN codes are 6 digits and never start with 0 or 9.
        if (pin.length !== 6 || !/^[1-8]/.test(pin)) {
          show('Please enter a valid 6-digit pincode.', true);
          return;
        }

        try { localStorage.setItem('mantraa_pin', pin); } catch (err) {}
        btn.disabled = true;
        var label = btn.textContent;
        btn.textContent = '…';

        function done() { btn.disabled = false; btn.textContent = 'Change'; }

        if (!token || !mid) { localEstimate(pin); done(); return; }

        var sign = self.byKey[self.selected] || {};
        fetch('https://api.gokwik.co/kwikship/v2/pincode/edd', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: '*/*', authorization: token },
          body: JSON.stringify({
            m_id: mid,
            pincode: pin,
            cart_total: sign.priceAmount || 0,
            type: 'pdp',
            items: [{ sku: sign.sku || '', quantity: 1 }],
            shopify_session_id: cookie('_shopify_s')
          })
        })
          .then(function (r) { return r.json(); })
          .then(function (j) {
            var d = j && j.data && j.data[0];
            if (!d || !d.text) throw new Error('no edd');
            show('Free Delivery by <b>' + String(d.text).replace(/^delivery by\s*/i, '') + '</b>', false);
            done();
          })
          .catch(function () {
            // GoKwik unreachable or pincode unserviceable: fall back quietly.
            localEstimate(pin);
            done();
          });
      }

      btn.addEventListener('click', check);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); check(); }
      });

      try {
        var saved = localStorage.getItem('mantraa_pin');
        if (saved) { input.value = saved; check(); }
      } catch (err) {}
    }

    /*
     * The sign's own product photography, captioned. Uses whatever images the
     * product actually has, so it stays right when the catalogue changes.
     */
    _renderRail(sign) {
      var host = this.$.rail;
      if (!host) return;
      var caps = this.railCaptions || [];
      var imgs = sign.gallery && sign.gallery.length ? sign.gallery : [sign.image];
      host.innerHTML = imgs.map(function (src, i) {
        var cap = caps[i] || '';
        return '<div class="mr-rail-item">'
          + '<div class="mr-rail-img" style="background-image:url(\'' + src + '\')"></div>'
          + (cap ? '<div class="mr-rail-cap">' + cap + '</div>' : '')
          + '</div>';
      }).join('');
      host.scrollLeft = 0;
    }

    _renderBenefits(sign) {
      var host = this.$.benefits;
      if (!host) return;
      host.innerHTML = (sign.benefits || []).map(function (b) {
        return '<div class="mr-benefit">' + b + '</div>';
      }).join('');
    }

    /*
     * Birthday finder. Most shoppers know the date but not the sign, so this
     * turns a birthday into the right rashi and spins the dial to it.
     */
    _initFinder() {
      var mSel = this.$.finderMonth, dSel = this.$.finderDay, out = this.$.finderOut;
      if (!mSel || !dSel) return;
      var self = this;

      this.signs.forEach(function (s) { s._range = parseRange(s.dates); });

      function fillDays() {
        var month = parseInt(mSel.value, 10);
        var max = month ? DAYS_IN[month - 1] : 31;
        var keep = parseInt(dSel.value, 10);
        var html = '<option value="">Day</option>';
        for (var d = 1; d <= max; d++) {
          html += '<option value="' + d + '"' + (d === keep ? ' selected' : '') + '>' + d + '</option>';
        }
        dSel.innerHTML = html;
      }

      function match() {
        var month = parseInt(mSel.value, 10);
        var day = parseInt(dSel.value, 10);
        if (!month || !day) { if (out) out.textContent = ''; return; }

        var hit = null;
        for (var i = 0; i < self.signs.length; i++) {
          if (inRange(self.signs[i]._range, month, day)) { hit = self.signs[i]; break; }
        }
        if (!hit) { if (out) out.textContent = ''; return; }

        if (out) {
          out.textContent = (self.getAttribute('data-finder-result') || 'Your rashi is')
            + ' ' + hit.name + (hit.hindi ? ' \u00B7 ' + hit.hindi : '');
        }
        self._endNudge();
        self.bringToCentre(hit.key);
        self.applySign(hit.key, { persist: true });
      }

      fillDays();
      mSel.addEventListener('change', function () { fillDays(); match(); });
      dSel.addEventListener('change', match);
    }

    /* Background starfield. Only rendered when the night skin is on. */
    _paintStars() {
      var host = this.$.stars;
      if (!host) return;
      var html = '';
      makeStars(64, 7).forEach(function (st) {
        html += '<span class="mr-star" style="left:' + st.left + '%;top:' + st.top
          + '%;width:' + st.size + 'px;height:' + st.size + 'px;animation:mr-twinkle '
          + st.dur + 's ease-in-out ' + st.delay + 's infinite"></span>';
      });
      host.innerHTML = html;
    }

    /*
     * Draw the sign's constellation behind the dial: a traced polyline plus a
     * star at every vertex. Points are percentages, so it scales with the box.
     */
    _drawConstellation(points) {
      points = points || [];
      var line = this.$.constelLine;
      var stars = this.$.constelStars;

      if (line) {
        line.setAttribute('points', points.map(function (p) { return p[0] + ',' + p[1]; }).join(' '));
        // Restart the trace animation on every sign change.
        line.style.animation = 'none';
        void line.offsetWidth;
        line.style.animation = '';
      }

      if (stars) {
        var html = '';
        points.forEach(function (p, i) {
          html += '<span class="mr-constel-star" style="left:' + p[0] + '%;top:' + p[1]
            + '%;animation:mr-twinkle ' + (2.8 + (i % 4) * 0.6).toFixed(1)
            + 's ease-in-out ' + (i * 0.3).toFixed(1) + 's infinite"></span>';
        });
        stars.innerHTML = html;
      }
    }

    /* --------------------------------------------------------------- chrome */

    _startNudge() {
      if (this.reduce) return;
      var self = this;
      this._nudgeTimer = setTimeout(function () {
        self._nudge();
        self._nudgeInterval = setInterval(function () {
          if (self._touched) return clearInterval(self._nudgeInterval);
          self._nudge();
        }, 4000);
      }, 1500);
    }

    _endNudge() {
      this._touched = true;
      clearTimeout(this._nudgeTimer);
      clearInterval(this._nudgeInterval);
      cancelAnimationFrame(this._nudgeRaf);
      this._nudging = false;
    }

    /* The wobble that teaches people the dial is draggable. */
    _nudge() {
      if (this._touched || this._nudging || document.hidden) return;
      this._nudging = true;

      var base = Math.round(this.offset / SPACING) * SPACING;
      var t0 = performance.now();
      var self = this;

      function step(now) {
        if (self._touched) {
          self.offset = base; self.renderDial(); self._nudging = false; return;
        }
        var p = Math.min(1, (now - t0) / 1150);
        self.offset = base + 17 * Math.sin(p * Math.PI * 2) * (1 - p);
        self.renderDial();
        if (p < 1) self._nudgeRaf = requestAnimationFrame(step);
        else { self.offset = base; self.renderDial(); self._nudging = false; }
      }
      this._nudgeRaf = requestAnimationFrame(step);
    }

    _scrollCheck() {
      var anchor = this.$.add, bar = this.$.bar;
      if (!anchor || !bar) return;

      var show = anchor.getBoundingClientRect().bottom < 8;
      if (show && this.$.finalRef) {
        var r = this.$.finalRef.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) show = false;
      }
      bar.classList.toggle('is-shown', show);
    }
  }

  window.customElements.define('mantraa-rashi', MantraaRashi);

  /* ==================================================================
   * <mantraa-pdp> — the same modules for any ordinary product page.
   * Drives the delivery estimate, coupon copy, pack selector, add-on
   * checkbox and urgency counter without needing the rashi dial.
   * ================================================================== */

  /*
   * Simulated stock counter.
   *
   * NOTE FOR FUTURE MAINTAINERS: this number is generated, not real. It is
   * seeded from the product id so every visitor sees the same figure, and it
   * ticks down with wall-clock time. Switch the block to "Real Shopify
   * inventory" in the theme editor to show actual stock instead.
   */
  function simulatedStock(el) {
    var seed = parseInt(el.getAttribute('data-stock-seed') || '1', 10);
    var min = parseInt(el.getAttribute('data-stock-min') || '12', 10);
    var max = parseInt(el.getAttribute('data-stock-max') || '60', 10);
    var tpl = el.getAttribute('data-stock-text') || 'Only [count] left in stock';
    var textEl = el.querySelector('[data-mr-stock-text]');
    var barEl = el.querySelector('[data-mr-stock-bar]');

    if (max <= min) max = min + 1;

    // Per-product jitter so twelve products don't all show the same figure.
    var x = Math.sin(seed % 100000) * 10000;
    var frac = x - Math.floor(x);
    var start = Math.round(max - frac * (max - min) * 0.35);
    var span = Math.max(1, start - min);

    function render() {
      // Declines across the day and refills at midnight, so it never sticks
      // at the floor the way a fixed per-hour drop does.
      var hours = (Date.now() / 3600000) % 24;
      var n = Math.round(start - span * (hours / 24));
      if (n < min) n = min;
      if (textEl) textEl.textContent = tpl.replace('[count]', n);
      if (barEl) barEl.style.width = Math.max(8, Math.round((n / max) * 100)) + '%';
    }
    render();
    return setInterval(render, 60000);
  }

  class MantraaPdp extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.$ = {};
      ['eddInput', 'eddBtn', 'eddOut', 'upsell', 'stars'].forEach(function (k) {
        this.$[k] = this.querySelector('[data-mr="' + k + '"]');
      }, this);

      this.selected = null;
      this.byKey = {};
      this.sku = this.getAttribute('data-sku') || '';
      this.priceAmount = parseFloat(this.getAttribute('data-price') || '0');

      this._initOffers();
      this._initEdd();
      this._initPacks();
      this._initUpsell();
      this._initStock();
      this._initCountdown();
      this._initReadMore();
      this._paintStars();
      this._celebrate();
      this.classList.add('is-ready');
    }

    _initReadMore() {
      var host = this.querySelector('[data-mr-readmore]');
      if (!host || host.getAttribute('data-rm-done')) return;

      // The description belongs to the theme, not to us, so look in the shared
      // product column rather than inside this host.
      var col = this.closest('.product__info-container') || document;
      var desc = col.querySelector('.product__description');
      if (!desc) return;

      var max = parseInt(host.getAttribute('data-rm-height') || '150', 10);
      // Nothing worth hiding — leave short descriptions alone entirely.
      if (desc.scrollHeight <= max + 24) return;

      host.setAttribute('data-rm-done', '1');
      var more = host.getAttribute('data-rm-more') || 'Read more';
      var less = host.getAttribute('data-rm-less') || 'Show less';

      desc.classList.add('mr-rm');
      desc.style.setProperty('--mr-rm-h', max + 'px');
      desc.style.setProperty('--mr-rm-fade', host.getAttribute('data-rm-fade') || '#FFF9F0');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mr-rm-btn';
      btn.textContent = more;
      btn.setAttribute('aria-expanded', 'false');
      desc.insertAdjacentElement('afterend', btn);

      btn.addEventListener('click', function () {
        var open = desc.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? less : more;
        // Collapsing from far down the description would otherwise leave the
        // reader staring at whatever happened to be below it.
        if (!open) desc.scrollIntoView({ block: 'nearest' });
      });
    }

    disconnectedCallback() {
      clearInterval(this._stockTimer);
      clearInterval(this._saleTimer);
    }

    /* The sale banner reuses the same countdown contract as the rashi page. */
    _initCountdown() {
      var wrap = this.querySelector('[data-mr="countdown"]');
      var clock = this.querySelector('[data-mr="countdownClock"]');
      if (!wrap || !clock) return;

      var mode = this.getAttribute('data-countdown-mode') || 'off';
      if (mode === 'off') return;

      var self = this;
      var resetHour = parseInt(this.getAttribute('data-countdown-hour') || '23', 10);

      function istDeadline(hour) {
        var now = new Date();
        var shift = (330 + now.getTimezoneOffset()) * 60000;
        var ist = new Date(now.getTime() + shift);
        ist.setHours(hour, 59, 59, 999);
        if (ist.getTime() <= now.getTime() + shift) ist.setDate(ist.getDate() + 1);
        return ist.getTime() - shift;
      }

      function target() {
        if (mode === 'daily') return istDeadline(resetHour);
        return Date.parse(self.getAttribute('data-deadline') || '');
      }

      var end = target();
      if (isNaN(end)) return;

      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          if (mode === 'daily') { end = target(); return; }
          wrap.classList.remove('is-live');
          clearInterval(self._saleTimer);
          return;
        }
        var d = Math.floor(left / 86400000);
        var h = Math.floor((left % 86400000) / 3600000);
        var m = Math.floor((left % 3600000) / 60000);
        var sec = Math.floor((left % 60000) / 1000);
        clock.textContent = (d > 0 ? d + 'd : ' : '')
          + pad(h) + 'h : ' + pad(m) + 'm : ' + pad(sec) + 's';
        wrap.classList.add('is-live');
      }
      tick();
      this._saleTimer = setInterval(tick, 1000);
    }

    /*
     * One-off celebration on arrival — a brief shower of gold sparks over the
     * page, then it removes itself. Fires once per browser session so a
     * returning visitor is not confettied on every navigation, and is skipped
     * entirely for anyone who prefers reduced motion.
     */
    _celebrate() {
      if (this.getAttribute('data-celebrate') !== 'true') return;
      if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      try {
        if (sessionStorage.getItem('mantraa_celebrated')) return;
        sessionStorage.setItem('mantraa_celebrated', '1');
      } catch (err) { /* private mode: just play it */ }

      var layer = document.createElement('div');
      layer.className = 'mr-celebrate';
      layer.setAttribute('aria-hidden', 'true');

      var COUNT = 26;
      var html = '';
      for (var i = 0; i < COUNT; i++) {
        var left = Math.round(Math.random() * 100);
        var delay = (Math.random() * 0.7).toFixed(2);
        var dur = (2 + Math.random() * 1.4).toFixed(2);
        var size = (5 + Math.random() * 6).toFixed(1);
        var drift = Math.round((Math.random() - 0.5) * 120);
        var spin = Math.round((Math.random() - 0.5) * 540);
        html += '<i style="left:' + left + '%;width:' + size + 'px;height:' + size
          + 'px;animation-delay:' + delay + 's;animation-duration:' + dur
          + 's;--dx:' + drift + 'px;--spin:' + spin + 'deg"></i>';
      }
      layer.innerHTML = html;
      document.body.appendChild(layer);

      // Clean up so nothing lingers in the DOM once the animation is done.
      setTimeout(function () {
        if (layer.parentNode) layer.parentNode.removeChild(layer);
      }, 4200);
    }

    _initStock() {
      var host = this.querySelector('[data-mr-stock][data-stock-mode="simulated"]');
      if (host) this._stockTimer = simulatedStock(host);
    }

    /* Writes the chosen quantity into the theme's own product form. */
    _initPacks() {
      var wrap = this.querySelector('[data-mr="packs"]');
      if (!wrap) return;
      var buttons = Array.prototype.slice.call(wrap.querySelectorAll('[data-mr-pack]'));

      function applyQty(qty) {
        var inputs = document.querySelectorAll(
          'form[action*="/cart/add"] input[name="quantity"], quantity-input input[name="quantity"]'
        );
        Array.prototype.forEach.call(inputs, function (input) {
          input.value = qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          buttons.forEach(function (b) { b.classList.toggle('is-on', b === btn); });
          applyQty(btn.getAttribute('data-mr-pack'));
        });
      });
    }

    /*
     * On a normal product page the theme owns the buy button, so the add-on
     * is injected just before that form submits and the submit then proceeds.
     */
    _initUpsell() {
      var box = this.$.upsell;
      if (!box) return;
      var self = this;

      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
        if (form.closest('mantraa-rashi')) return;       // the rashi form handles its own
        if (!box.checked || self._upsellDone) return;

        e.preventDefault();
        self._upsellDone = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(box.value, 10), quantity: 1 }] })
        })
          .catch(function () { /* still let the main product through */ })
          .then(function () {
            if (typeof form.requestSubmit === 'function') form.requestSubmit();
            else form.submit();
          });
      }, true);
    }
  }

  MantraaPdp.prototype._paintStars = MantraaRashi.prototype._paintStars;
  MantraaPdp.prototype._initOffers = MantraaRashi.prototype._initOffers;
  MantraaPdp.prototype._initEdd = MantraaRashi.prototype._initEdd;

  window.customElements.define('mantraa-pdp', MantraaPdp);

  /* Standalone FAQ accordion, used by sections/mantraa-faq.liquid. */
  document.addEventListener('click', function (e) {
    var q = e.target.closest && e.target.closest('[data-mr-faq]');
    if (!q) return;
    var item = q.parentElement;
    var open = item.classList.toggle('is-open');
    q.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
})();
